<!-- <template>
  <ul class="menu">
    <li v-for="menuItem in menus" @click="click(menuItem, $event)" :class="{disabled: menuItem.disabled}">{{ menuItem.title }}</li>
  </ul>
</template> -->

<!--

| key                                      | purpose                                                                                              | where used                     |
| ---------------------------------------- | ---------------------------------------------------------------------------------------------------- | ------------------------------ |
| `type` (`'separator'`)                   | render divider instead of a normal item                                                              | `if (m.type == 'separator') …` |
| `title` (string)                         | text shown for the item                                                                              | `m.title`                      |
| `subtitle` (string)                      | secondary text aligned at the end of the row                                                         | `m.subtitle`                   |
| `icon` (string ∣ VNode)                  | glyph placed before the title                                                                        | `if (m.icon) …`                |
| `action(event)`                          | callback run on click                                                                                | `const cb = menuItem.action`   |
| `disabled` (bool)                        | greys out item, blocks click                                                                         | `if (m.disabled) …`            |
| `class` (string ∣ array)                 | extra CSS classes such as `"danger"`                                                                 | `if (m.class) classes.push…`   |
| `value` (any)                            | makes list behave like a `<select>`;                                                                 |                                |
|                                          | selected value is compared to `modelValue` and emitted on click | `hasOwnProperty('value')`          |                                |
| `checked` (wrapper with `get()`/`set()`) | shows a live checkbox, toggles state on click                                                        | `if (m.checked) …`             |

 -->

<style lang="scss">

@keyframes menu-flash {
  0%, 100% { background: inherit; }     /* normal state */
  25%, 75% { background: var(--accent-color); }
}

.menu {
  
  list-style: none;
  user-select: none;
  margin: 0 -9px; padding: 0;
  color: var(--text-color);
  
  li { 
    display: flex;
    align-items: center;
    text-align: left; 
    padding: 4px 10px;
    white-space: nowrap; 
    min-width: 200px; 
    cursor: default; 
    
    &:hover:not(.disabled)    { cursor: pointer; color: #fff; background: var(--accent-color); }
    &.selected,
    &:active:not(.disabled)   { color: #ddd; background: var(--dark-accent-color); }
    &.disabled { 
      color: var(--text-dim3-color); 
      cursor: default;
      &:hover,
      &:active { background: inherit; font-weight: normal;}
    }
    
    &.selected-option,
    &.unselected-option { 
      &:before {
        content: ' ';
        display: inline-block;
        width: 1rem;
        margin: 0 0.2ex;
      }
    }
    &.selected-option{
      font-weight: bold;
      &:before { content: '✔';}
    }
    
    &.menu-separator {
      user-select: none;
      background: transparent !important;
      cursor: default; 
      
      hr {
        flex: 1;
        border: none;
        border-bottom: 1px solid var(--text-dim3-color); 
      }
    }
    
    &.flash {
      animation: 125ms ease-in-out infinite menu-flash;   /* ~½ s total, two blinks */
    }
  }

  .danger       { color: var(--button-danger-bgcolor) }
  .danger:hover {  background: var(--button-danger-bgcolor) !important; }
  .icon { font-family: 'Font Awesome 5 Free'; width: 1.25em; display: inline-block; font-size: 1rem; margin-right: 1ex;}
  .subtitle {
    margin-left: auto;
    padding-left: 1.5rem;
    color: var(--text-dim1-color);
    font-weight: normal;
  }
}



</style>

<script>
  import { h } from 'vue'

  export default {
    props: {
      menus: {
        type: Array,
        required: true
      },
      
      modelValue: {
        required: false,
        default: undefined,
      }
    },
    
    emits: ['update:modelValue', 'change'],
    
    // inject: ['popover'],
    
    methods: {
      dateTimeValue(value) {
        if (value instanceof Date) return value.getTime();
        if (typeof value !== 'string') return undefined;

        const time = Date.parse(value);
        return Number.isNaN(time) ? undefined : time;
      },

      valuesAreEqual(a, b) {
        if (a === b) return true;

        const aTime = this.dateTimeValue(a);
        const bTime = this.dateTimeValue(b);
        if (aTime !== undefined || bTime !== undefined) {
          return aTime !== undefined && bTime !== undefined && aTime === bTime;
        }

        return a == b;
      },

      async clickMenuItem(menuItem, event) {
        console.debug("click menuItem", menuItem, event);
        if (menuItem.disabled || menuItem.type == 'separator') {
          return;
        }
        
        if (menuItem.hasOwnProperty('value')) {
          if (!this.valuesAreEqual(menuItem.value, this.modelValue)) {
            this.$emit('update:modelValue', menuItem.value);
            this.$emit('change', menuItem.value);
          }
          this.closeIfInPopover();
          return;
        }
        
        if (menuItem.checkboxVNode) {
          console.debug("click found checkbox; delegating")
          menuItem.checkboxVNode.elm.click();
          return;
        }
        
        const cb = menuItem.action;
        if (!cb) return;

        cb(event);
        
        // flash
        await this.flashRow(event.target);
        
        // Handle popovers
        this.closeIfInPopover();
      },
      
      flashRow(elm) {
        if (!elm) 
          return;
        
        elm.classList.add('flash');
        return new Promise((resolve) => {
          setTimeout(() => {
            elm.classList.remove('flash');
            resolve();
          }, 225);
        });
      },
      
      closeIfInPopover() {
        let p = this.$parent;
        while (p) {
          if (p.isPopover) {
            p.hide();
            break;
          }
          p = p.$parent;
        }
      }
    },
    
    render() {
      const clickHandler = this.clickMenuItem;
      const modelValue = this.modelValue;
      const children = this.menus.map(m => {
        
        let itemElms = [];
        
        if (m.type == 'separator') {
          return h('li', {class: 'menu-separator'}, [h('hr')]);
        }
        else {
          let classes = []
          if (m.disabled) classes.push('disabled');
          if (m.class) classes.push(m.class);
          
          if (m.hasOwnProperty('value')) {
            // We're showing what's essentially a <select>
            classes.push(this.valuesAreEqual(m.value, this.modelValue) ? 'selected-option' : 'unselected-option');
          }
          
          // An actual checkbox
          if (m.checked) {
            m.checkboxVNode = h('input', { 
              attrs: { 
                type: 'checkbox', 
                checked: m.checked.get()
              },
              onInput: (e) => m.checked.set(e.target.checked),
            });

            itemElms.push(m.checkboxVNode);
          }
          
          let title;
          if (!m.title) {
            console.warn("Menu lacks a title", m);
            m.title = '';
          }
          if (m.icon || m.iconColor) {
            title = [
              h('span', { class:'icon', style: `--color:${m.iconColor}` }, m.icon ? m.icon : '\u00A0'),
              h('span', { class:'title' }, m.title.trim())
            ]
          }
          else {
            title = h('span', { class:'title' }, m.title);
          }
          itemElms.push(title);
          if (m.subtitle) {
            itemElms.push(h('span', { class: 'subtitle' }, m.subtitle));
          }

          return h('li', {
            class: classes,
            onClick: clickHandler.bind(this, m),
          }, itemElms );
        }
      });
      
      let menuClass = 'menu';
      if (this.modelValue !== undefined) {
        menuClass += ' options'
      }
      return h('ul', {class: 'menu'}, children);
    }
  }
</script>
